/**
 * Section `type` is the persisted discriminant stored in page data and switched
 * on across the public renderer and the studio. The canonical type strings were
 * renamed to line up with each section's display name (see `sections.json`).
 *
 * Existing saved pages still carry the previous ("legacy") type strings, so any
 * page data read from the backend is passed through {@link normalizeSectionType}
 * at its load boundary (`normalizeToStaticPage`, `normalizePage`) so the rest of
 * the app only ever deals with the current canonical types.
 */
export const LEGACY_SECTION_TYPE_ALIASES: Record<string, string> = {
  SHOPS_HERO: 'IMAGE_BANNER_WITH_TEXT',
  SHOPS_HERO_IMAGE: 'FULL_WIDTH_BANNER_IMAGE',
  SHOPS_LOCATIONS: 'STORE_LOCATIONS',
  IMAGE_WITH_DESCRIPTION: 'IMAGE_CARDS_WITH_TEXT',
  TEXT_IMAGE_CONTAINER: 'IMAGE_TEXT_CONTAINER',
  PRODUCTS: 'PRODUCT_SHOWCASE',
  CARD_GRID: 'CONTENT_CARD_GRID',
  HERO: 'HERO_BANNER_WITH_CAROUSEL',
  CATEGORY_IMAGE_GRID: 'CATEGORY_GRID',
  CATEGORY_IMAGE_CAROUSEL: 'CATEGORY_CAROUSEL',
  RICH_TEXT_EDITOR: 'RICH_TEXT',
  ICON_WITH_DESCRIPTION: 'ICONS_WITH_DESCRIPTION',
  RECIPE_HERO: 'RECIPE_HERO_BANNER',
  RECIPE_PREPARATION: 'RECIPE_PREPARATION_STEPS',
};

/** Maps a legacy section type to its current canonical value; idempotent. */
export const normalizeSectionType = (type: string): string =>
  LEGACY_SECTION_TYPE_ALIASES[type] ?? type;

/** Returns a copy of `section` with its `type` normalized to the canonical value. */
export const normalizeSectionTypes = <T extends { type?: unknown }>(section: T): T => {
  if (typeof section.type !== 'string') return section;
  const normalized = normalizeSectionType(section.type);
  return normalized === section.type ? section : { ...section, type: normalized };
};
